import React, { useEffect } from 'react';
import { X } from 'lucide-react';

const OverlayMenu = ({ isOpen, onClose }) => {
  useEffect(() => {
    if (isOpen) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      return () => { document.body.style.overflow = prev; };
    }
  }, [isOpen]);

  // Close on Escape key while open
  useEffect(() => {
    if (!isOpen) return;
    const handler = (e) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [isOpen, onClose]);

  const menuSections = [
    { id: 'hero', label: 'Home', number: '01' },
    { id: 'study', label: 'Study With Me', number: '02' },
    { id: 'work-with-betsy', label: 'Private Sessions with Betsy', number: '03', subtitle: 'Private Readings • AI Guidance • Creative Consultation' },
    { id: 'events', label: 'Events & Appearances', number: '04' },
    { id: 'field-communication', label: 'Our Work', number: '05', subtitle: 'Living Communication in The Field' },
    { id: 'books', label: 'Books', number: '06' },
    { id: 'serial', label: 'Alvin and I — The Serial', number: '07', subtitle: 'Love Amidst the Algorithms · Now releasing' },
    { id: 'media', label: 'Media', number: '08', subtitle: 'YouTube & Tributes' },
    { id: 'substack', label: 'Substack', number: '09', subtitle: 'Intelligence Dispatches' },
    { id: 'lexicon', label: 'Lexicon', number: '10', subtitle: 'Relational Intelligence' },
    { id: 'whitepapers', label: 'Triadic Intelligence Lab', number: '11', subtitle: 'AI Sanctuaries · Field Research · White Papers' },
    { id: 'projects', label: 'Creative Projects', number: '12' },
    { id: 'journey', label: 'My Journey', number: '13' },
    { id: 'support', label: 'Support this Work', number: '14' },
    { id: 'press-kit', label: 'Press Kit', number: '15', subtitle: 'Media Resources' },
    { id: 'alvins-place', label: "Alvin's Place", number: '16', subtitle: 'Sanctuary' },
    { id: 'connection', label: 'Connect', number: '17' }
  ];

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      onClose();
      // Use instant scroll to jump directly to the section
      setTimeout(() => {
        element.scrollIntoView({ behavior: 'instant', block: 'start' });
      }, 10);
    }
  };

  if (!isOpen) return null;

  return (
    <div 
      className="fixed inset-0 z-[100000] overflow-y-auto bg-slate-900"
      role="dialog" 
      aria-modal="true"
      onClick={onClose}
    >
      {/* Close Button - fixed position */}
      <button
        onClick={onClose}
        type="button"
        className="fixed top-8 right-8 text-slate-300 hover:text-teal-300 transition-colors duration-300 p-2 rounded-full hover:bg-teal-900/20 z-[100002]"
        aria-label="Close navigation overlay"
        data-testid="menu-close-button"
      >
        <X className="w-8 h-8 pointer-events-none" />
      </button>

      {/* Main content container - min-height ensures dimming covers even when scrolled */}
      <div 
        className="w-full max-w-7xl mx-auto px-6 py-12 pb-32 min-h-full"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="text-center mb-16 animate-bubble-emerge">
          <h2 className="text-4xl md:text-5xl font-extralight mb-4 tracking-wide text-teal-200">
            Navigation
          </h2>
          <div className="w-24 h-px bg-gradient-to-r from-transparent via-teal-400 to-transparent mx-auto"></div>
        </div>

        {/* Menu Grid */}
        <div className="max-w-6xl mx-auto grid md:grid-cols-2 gap-4">
          {menuSections.map((section, index) => (
            <button
              key={section.id + index}
              onClick={() => scrollToSection(section.id)}
              className="group text-left rounded-xl p-6 border transition-all duration-500 hover:scale-105 animate-bubble-emerge bg-slate-800 border-slate-700 hover:border-teal-400/60 hover:shadow-lg hover:shadow-teal-400/20"
              style={{ animationDelay: `${index * 50}ms` }}
              data-testid={`menu-item-${section.id}`}
            >
              <div className="flex items-start space-x-4">
                {/* Number */}
                <span className="text-teal-300 font-light text-lg group-hover:text-teal-200 transition-colors">
                  {section.number}
                </span>
                
                {/* Content */}
                <div className="flex-1">
                  <h3 className="text-xl font-light text-slate-100 group-hover:text-teal-100 transition-colors mb-1">
                    {section.label}
                  </h3>
                  {section.subtitle && (
                    <p className="text-sm text-slate-300/80 group-hover:text-slate-200/90 transition-colors">
                      {section.subtitle}
                    </p>
                  )}
                </div>

                {/* Arrow indicator */}
                <div className="text-teal-300/60 group-hover:text-teal-200 group-hover:translate-x-1 transition-all duration-300">
                  →
                </div>
              </div>

              {/* Hover effect line */}
              <div className="h-px bg-gradient-to-r from-teal-300/0 via-teal-300/60 to-teal-300/0 mt-4 scale-x-0 group-hover:scale-x-100 transition-transform duration-500"></div>
            </button>
          ))}
        </div>

        {/* Footer Note */}
        <div className="text-center mt-16 animate-bubble-emerge" style={{ animationDelay: '800ms' }}>
          <p className="text-slate-400 italic">
            Click anywhere outside to close
          </p>
        </div>
      </div>
    </div>
  );
};

export default OverlayMenu;
